import React, { useState } from 'react';
import { Policy } from '../../services/firestoreService';
import { Card, CardContent } from '../ui/card';
import { ChevronRight, Upload } from 'lucide-react';
import JoinWaitlistModal from '../ui/JoinWaitlistModal';

interface PoliciesTabProps {
  policies: Policy[];
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
}

const PoliciesTab: React.FC<PoliciesTabProps> = ({
  policies,
  selectedCategory,
  onCategoryChange
}) => {
  // 隱藏/顯示數字的狀態
  const [isHidden, setIsHidden] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: '', message: '' });

  // 切換隱藏/顯示狀態
  const toggleVisibility = () => {
    setIsHidden(!isHidden);
  };

  // 格式化數字顯示
  const formatAmount = (amount: number) => {
    if (isHidden) {
      return '***';
    }
    return amount.toLocaleString();
  };

  // 上傳按鈕點擊處理
  const handleUploadClick = () => {
    setModalConfig({
      title: 'Join waitlist',
      message: 'This is a test version with limited functionality for demonstration purposes!\n\nFuture versions will support PDF policy file uploads.\n\nPlease click "Join Waitlist" to stay updated on our development progress!'
    });
    setShowWaitlistModal(true);
  };

  // 查看保單詳情按鈕點擊處理 - 與桌面版相同
  const handleViewDetailsClick = () => {
    setModalConfig({
      title: 'Join waitlist',
      message: 'This is a test version with limited functionality for demonstration purposes!\n\nFuture versions will support viewing policy summaries and detailed content.\n\nPlease click "Join Waitlist" to stay updated on our development progress!'
    });
    setShowWaitlistModal(true);
  };

  // 處理加入等待列表
  const handleJoinWaitlist = () => {
    window.open('https://cop855u0.forms.app/surepal-waitlist', '_blank');
    setShowWaitlistModal(false);
  };

  // 關閉彈出視窗
  const handleCloseModal = () => {
    setShowWaitlistModal(false);
  };
  // 分類保單
  const categorizePolicy = (policy: Policy): string => {
    const name = policy.name.toLowerCase();
    if (name.includes('health') || name.includes('medical')) return 'Health';
    if (name.includes('life')) return 'Life';
    if (name.includes('disability')) return 'Social';
    if (name.includes('pet')) return 'Liability';
    if (name.includes('dental')) return 'Health';
    if (name.includes('accident')) return 'Accident';
    if (name.includes('unemployment')) return 'Social';
    return 'Other';
  };

  // 按分類分組保單
  const groupedPolicies = policies.reduce((acc, policy) => {
    const category = categorizePolicy(policy);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(policy);
    return acc;
  }, {} as Record<string, Policy[]>);

  // 對每個分類的保單進行排序：保障中的保單排在已到期的保單前面
  Object.keys(groupedPolicies).forEach(category => {
    groupedPolicies[category].sort((a, b) => {
      const aIsExpired = a.status === 'expired';
      const bIsExpired = b.status === 'expired';
      
      // 如果一個已到期，一個保障中，保障中的排在前面
      if (aIsExpired && !bIsExpired) return 1;
      if (!aIsExpired && bIsExpired) return -1;
      
      // 如果狀態相同，保持原順序
      return 0;
    });
  });

  const categories = [
    { key: 'Health', label: 'Health' },
    { key: 'Life', label: 'Life' },
    { key: 'Social', label: 'Social' },
    { key: 'Liability', label: 'Liability' },
    { key: 'Accident', label: 'Accident' },
    { key: 'Other', label: 'Other' }
  ];

  // 計算總保費
  const totalMonthlyPremium = policies.reduce((sum, policy) => {
    return sum + (policy.monthlyPremium || 0);
  }, 0);

  const totalAnnualPremium = policies.reduce((sum, policy) => {
    return sum + (policy.annualPremium || 0);
  }, 0);

  return (
    <div className="h-full flex flex-col">
      {/* 固定置頂的總計區域 - 佔據螢幕20%高度 */}
      <div 
        className="px-6 border-b flex-shrink-0 relative z-10 flex items-center justify-center overflow-hidden"
        style={{ 
          backgroundColor: 'white', 
          borderColor: 'var(--card-shadow)',
          background: 'white',
          height: 'calc(var(--vh, 1vh) * 20)', // 佔據螢幕高度的20%
          minHeight: '120px' // 最小高度確保內容不會被壓縮過度
        }}
      >
        <div className="text-center w-full max-w-sm">
          {/* 月繳保費 */}
          <div className="mb-2">
            <div className="text-xs mb-1" style={{ color: 'var(--medium-gray)' }}>Monthly Premium</div>
            <div className="flex items-center justify-center space-x-2">
              <div className="text-2xl font-bold" style={{ color: 'var(--charcoal)' }}>
                ${formatAmount(totalMonthlyPremium)}
              </div>
              <button 
                onClick={toggleVisibility}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                {isHidden ? (
                  // 閉眼圖標 - 更新為更清晰的閉眼設計
                  <svg className="w-4 h-4" style={{ color: 'var(--medium-gray)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M1 1l22 22" />
                  </svg>
                ) : (
                  // 睜眼圖標
                  <svg className="w-4 h-4" style={{ color: 'var(--medium-gray)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          
          {/* 年繳保費和上傳按鈕 */}
          <div className="flex items-center justify-center space-x-2">
            {/* 年繳保費 */}
            <div 
              className="px-3 py-1.5 rounded-lg flex items-center space-x-1 flex-shrink-0"
              style={{ 
                backgroundColor: 'var(--very-light-gray)',
                color: 'var(--charcoal)'
              }}
            >
              <span className="text-xs" style={{ color: 'var(--medium-gray)' }}>Annual</span>
              <span className="text-sm font-semibold">${formatAmount(totalAnnualPremium)}</span>
            </div>
            
            {/* 上傳按鈕 */}
            <button
              onClick={handleUploadClick}
              className="px-3 py-1.5 rounded-lg flex items-center space-x-1 text-white font-medium transition-colors hover:opacity-90 flex-shrink-0"
              style={{ 
                backgroundColor: 'var(--charcoal)' // 與桌面版一致的顏色
              }}
            >
              <Upload className="h-3 w-3" />
              <span className="text-xs">Upload</span>
            </button>
          </div>
        </div>
        
      </div>

      {/* 可滾動的保單列表區域 */}
      <div className="flex-1 overflow-y-auto relative" style={{ background: 'var(--main-background)' }}>
        {/* 分類列表 */}
        <div className="pt-2" style={{ background: 'var(--main-background)' }}>
          {categories.map((category) => {
            const categoryPolicies = groupedPolicies[category.key] || [];
            if (categoryPolicies.length === 0) return null;

            return (
              <div key={category.key} className="border-b" style={{ borderColor: 'var(--card-shadow)' }}>
                {/* 分類標題 */}
                <div className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <span className="font-medium" style={{ color: 'var(--charcoal)' }}>{category.label}</span>
                    <span className="text-sm" style={{ color: 'var(--medium-gray)' }}>({categoryPolicies.length})</span>
                  </div>
                </div>
                
                {/* 保單列表 */}
                <div className="px-6 pb-4 space-y-3">
                        {categoryPolicies.map((policy) => (
                          <Card 
                            key={policy.id} 
                            className="mb-3 cursor-pointer hover:shadow-lg transition-shadow" 
                            style={{ backgroundColor: 'white', boxShadow: '0 2px 8px var(--card-shadow)' }}
                            onClick={handleViewDetailsClick}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-semibold text-base mb-2 truncate" style={{ color: 'var(--charcoal)' }}>
                                    {policy.name}
                                  </h3>
                                  <p className="text-xs mb-2" style={{ color: 'var(--medium-gray)' }}>
                                    {policy.company || 'Unknown Company'}
                                  </p>
                                  <div className="text-sm font-medium" style={{ color: 'var(--charcoal)' }}>
                                    ${formatAmount(policy.monthlyPremium || 0)}/month
                                  </div>
                                </div>
                                <div className="ml-3 flex items-center justify-center w-8 h-8">
                                  <ChevronRight className="w-5 h-5" style={{ color: 'var(--medium-gray)' }} />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* 空狀態 */}
        {policies.length === 0 && (
          <div className="px-6 py-16 text-center">
            <div className="text-sm" style={{ color: 'var(--medium-gray)' }}>
              No policies available
            </div>
            <div className="text-xs mt-1" style={{ color: 'var(--light-gray)' }}>
              Upload policy files to start managing
            </div>
          </div>
        )}
      </div>

      {/* Join Waitlist Modal */}
      <JoinWaitlistModal
        isOpen={showWaitlistModal}
        onClose={handleCloseModal}
        onJoin={handleJoinWaitlist}
        title={modalConfig.title}
        message={modalConfig.message}
      />
    </div>
  );
};

export default PoliciesTab;
